import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  AddressBookProvider,
  EditAddressDrawer,
  type StorefrontAddress,
} from "shared-ui/src/shared-features/address-book";
import {
  QuoteAddressOption,
  useRecentQuoteAddresses,
  useSearchQuoteAddresses,
} from "./hooks/useQuoteAddresses";
import { getCustomerExperienceAddressBookApi } from "../../features/storefronts-feature/api/customerExperienceAddressBookApi";

function formatAddress(a: QuoteAddressOption): string {
  const parts = [a.street, a.street2, a.city, a.state, a.postalCode].filter(
    (p) => p && p.trim().length > 0,
  );
  return parts.join(", ");
}

// Group filter mirrors shi.com checkout: an address's groups are its org-unit address group
// names, falling back to bucketing ungrouped addresses by their state code.
function groupTags(a: QuoteAddressOption): string[] {
  if (a.addressGroups && a.addressGroups.length > 0) return a.addressGroups;
  if (a.state && a.state.trim().length > 0) return [a.state];
  return [];
}

function mapCreatedAddress(address: StorefrontAddress): QuoteAddressOption {
  return {
    id: Number(address.id),
    name: address.siteName,
    street: address.streetAddress,
    street2: address.streetAddress2,
    city: address.city,
    state: address.state,
    postalCode: address.zip,
    country: address.country,
    isDefault: address.isDefault ?? false,
  };
}

function AddressRadioCard({ address, selected }: { address: QuoteAddressOption; selected: boolean }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        px: 2,
        py: 1.5,
      }}
    >
      <FormControlLabel
        value={String(address.id)}
        control={<Radio />}
        sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
        label={
          <Box sx={{ pt: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {address.name || address.street || "Address"}
              </Typography>
              {address.isDefault && <Chip size="small" label="Default" color="primary" variant="outlined" />}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatAddress(address)}
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}

export default function AddressSelection({
  orgUnitId,
  selectedAddressId,
  onSelect,
}: {
  orgUnitId: number | null;
  selectedAddressId: number | null;
  onSelect: (address: QuoteAddressOption | null) => void;
}) {
  const [searchText, setSearchText] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Addresses created in-session aren't in the recent/search query results until those refetch, so
  // we hold them here and merge them into the list to keep a just-added (and selected) address visible.
  const [createdAddresses, setCreatedAddresses] = useState<QuoteAddressOption[]>([]);

  const recent = useRecentQuoteAddresses(orgUnitId);
  const search = useSearchQuoteAddresses(orgUnitId, searchText);

  const isSearching = searchText.trim().length > 0;
  const baseResults = isSearching ? search.addresses : recent.addresses;
  const results = useMemo(() => {
    if (createdAddresses.length === 0) return baseResults;
    const seen = new Set(baseResults.map((a) => a.id));
    return [...createdAddresses.filter((a) => !seen.has(a.id)), ...baseResults];
  }, [baseResults, createdAddresses]);

  // Only the active query drives loading/error; the inactive one is disabled
  // and must not gate the list (react-query v4 disabled-query semantics).
  const activeIsLoading = isSearching ? search.isLoading : recent.isLoading;
  const activeError = isSearching ? search.error : recent.error;

  const groupOptions = useMemo(
    () =>
      Array.from(new Set(results.flatMap(groupTags).filter((g) => g && g.trim().length > 0))).sort(),
    [results],
  );

  const visible = useMemo(
    () => (groupFilter ? results.filter((a) => groupTags(a).includes(groupFilter)) : results),
    [results, groupFilter],
  );

  const handleSelect = (id: string) => {
    const match = visible.find((a) => String(a.id) === id) ?? null;
    onSelect(match);
  };

  const handleAddressCreated = (address: QuoteAddressOption) => {
    setCreatedAddresses((prev) => (prev.some((a) => a.id === address.id) ? prev : [address, ...prev]));
    setSearchText("");
    setGroupFilter("");
    onSelect(address);
  };

  const handleAddressSaved = (address: StorefrontAddress) => {
    if (!Number.isFinite(Number(address.id)) || Number(address.id) <= 0) return;
    handleAddressCreated(mapCreatedAddress(address));
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={500}>
          Ship To Address
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setDrawerOpen(true)}
          disabled={orgUnitId == null}
          sx={{ whiteSpace: "nowrap" }}
        >
          Add New Address
        </Button>
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, alignItems: "center" }}>
        <TextField
          label="Search addresses"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setGroupFilter("");
          }}
          placeholder="Search by name, street, or city"
          fullWidth
          size="small"
          InputProps={{
            endAdornment: search.isFetching ? <CircularProgress size={16} /> : undefined,
          }}
        />
        <TextField
          select
          label="Group"
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          size="small"
          fullWidth
        >
          <MenuItem value="">All</MenuItem>
          {groupOptions.map((group) => (
            <MenuItem key={group} value={group}>
              {group}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ bgcolor: "background.default", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1.5 }}>
          {isSearching ? "Search results" : "Recently used"}
        </Typography>

        {activeIsLoading && visible.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : activeError ? (
          <Alert severity="error">Addresses could not be loaded. Try again.</Alert>
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {isSearching
              ? "No addresses match your search."
              : "No recently used addresses. Search above or add a new address."}
          </Typography>
        ) : (
          <RadioGroup value={selectedAddressId != null ? String(selectedAddressId) : ""} onChange={(e) => handleSelect(e.target.value)}>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {visible.map((address) => (
                <AddressRadioCard
                  key={address.id}
                  address={address}
                  selected={address.id === selectedAddressId}
                />
              ))}
            </Box>
          </RadioGroup>
        )}
      </Box>

      {drawerOpen && (
        <AddressBookProvider
          api={getCustomerExperienceAddressBookApi()}
          orgUnitId={orgUnitId == null ? null : String(orgUnitId)}
        >
          <EditAddressDrawer
            open
            onClose={() => setDrawerOpen(false)}
            mode="create"
            onSaved={() => undefined}
            onSave={handleAddressSaved}
            // Render above the enclosing Save-as-Quote dialog (MUI modal z-index 1300); a plain
            // Drawer sits at the lower drawer z-index and would otherwise open behind it.
            sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          />
        </AddressBookProvider>
      )}
    </Box>
  );
}
